/**
 * MobileTabBar — fixed bottom tab navigation for phone-width viewports.
 *
 * The desktop sidebar is context-scoped (portfolio / project / timesheet / …),
 * so the bottom bar deliberately uses GLOBAL personal destinations that are
 * meaningful in any context — plus a Menu button that opens the full nav drawer
 * (the context-scoped tree). This is the thumb-zone primary nav; the drawer is
 * the full map. Renders nothing on desktop.
 */

import { useLocation, useNavigate } from 'react-router-dom'
import { Home, Briefcase, Clock, Bell, Menu } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '@/store/appStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import * as ds from '@/services/dataService'
import { cn } from '@/lib/utils'
import { isPathAllowed, firstAllowedHome, useModuleEntitlement } from '@/config/moduleEntitlements'

interface TabItem {
  label: string
  icon: typeof Home
  path?: string
  match?: string
  onClick?: () => void
  /** Force the active highlight independent of the route (e.g. Menu = drawer open). */
  activeWhen?: boolean
}

export function MobileTabBar() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const setMobileNavOpen = useAppStore((s) => s.setMobileNavOpen)
  const mobileNavOpen = useAppStore((s) => s.mobileNavOpen)
  const managementContext = useAppStore((s) => s.managementContext)
  const entitlement = useModuleEntitlement()
  const currentUserId = useAppStore((s) => s.currentUser.id)

  const { data: rawNotifications } = useQuery({
    queryKey: ['notifications', currentUserId],
    queryFn: () => ds.getNotifications(),
    staleTime: 60_000,
    enabled: isMobile,
  })
  const unreadCount = (rawNotifications as Array<{ read: boolean; dismissed: boolean }> | undefined)
    ?.filter((n) => !n.read && !n.dismissed).length ?? 0

  if (!isMobile) return null

  // Home goes to the CURRENT context's root, not always the portfolio dashboard
  // — otherwise a timesheet/contract/config persona with no portfolio selected
  // gets bounced to the context picker by ProtectedRoute.
  const contextHomePath =
    managementContext === 'timesheet' ? '/timesheet'
    : managementContext === 'contract' ? '/contracts'
    : managementContext === 'supplychain' ? '/supply-chain'
    : managementContext === 'configuration' ? '/config'
    : managementContext === 'mywork' ? '/my-work'
    : managementContext === 'personnel' ? '/personnel'
    : '/dashboard'
  // A restricted user (module entitlements) whose context home is out of reach
  // goes to their first allowed module instead.
  const homePath = isPathAllowed(contextHomePath, entitlement) ? contextHomePath : firstAllowedHome(entitlement)

  const allTabs: TabItem[] = [
    { label: 'Home', icon: Home, path: homePath, match: homePath },
    { label: 'My Work', icon: Briefcase, path: '/my-work', match: '/my-work' },
    { label: 'Timesheet', icon: Clock, path: '/timesheet/my', match: '/timesheet' },
    { label: 'Alerts', icon: Bell, path: '/notifications', match: '/notifications' },
    // Menu toggles the nav drawer: press to open, press again to close.
    { label: 'Menu', icon: Menu, onClick: () => setMobileNavOpen(!mobileNavOpen), activeWhen: mobileNavOpen },
  ]
  // Hide shortcuts into modules this user is not entitled to.
  const tabs = allTabs.filter((t) => !t.path || isPathAllowed(t.path, entitlement))

  const isActive = (match?: string) =>
    !!match && (location.pathname === match || location.pathname.startsWith(match + '/'))

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border flex pb-safe-bottom"
      role="navigation"
      aria-label="Primary mobile navigation"
    >
      {tabs.map((tab) => {
        const active = tab.activeWhen ?? isActive(tab.match)
        return (
          <button
            key={tab.label}
            onClick={() => (tab.onClick ? tab.onClick() : tab.path && navigate(tab.path))}
            className={cn(
              'flex-1 min-h-[3.25rem] flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-all',
              active ? 'text-brand-600' : 'text-text-tertiary hover:text-text-secondary',
            )}
            aria-label={tab.label}
            aria-current={active ? 'page' : undefined}
          >
            <span className="relative">
              <tab.icon size={20} />
              {tab.label === 'Alerts' && unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1 ring-2 ring-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </span>
            <span className="text-[10px] font-medium leading-none">{tab.label}</span>
          </button>
        )
      })}
    </nav>
  )
}
